import {
  ExchangeFormData,
  ExchangeSummary,
  SalesOrder,
  SelectedProduct,
} from '../../../../types/sales.types';
import { roundMoney } from '../../../../utils/money';
import {
  computeNewItemsAdjustedTotal,
  computeNewItemsCatalogTotal,
  computeNewItemsExchangeTotal,
  computeNewItemsMarkupAmount,
  computeOriginalItemsTotal,
  computePaidForExchangedItems,
  getExchangeAppliedDiscount,
  isExchangeSummaryCoherent,
} from './exchangePricing.utils';

export const EMPTY_EXCHANGE_SUMMARY: ExchangeSummary = {
  originalItemsTotal: 0,
  paidForExchangedItems: 0,
  newItemsTotal: 0,
  newItemsSubtotal: 0,
  markupAmount: 0,
  discountAmount: 0,
  difference: 0,
  requiresPayment: false,
  requiresRefund: false,
  totalPayments: 0,
  remainingBalance: 0,
  change: 0,
  paymentStatus: 'pending',
  isSummaryCoherent: true,
};

export function buildExchangeSummary(
  order: SalesOrder | null,
  exchangeData: ExchangeFormData,
  resolveFinalPrice: (product: SelectedProduct) => number,
): ExchangeSummary {
  if (!order) {
    return EMPTY_EXCHANGE_SUMMARY;
  }

  const originalItemsTotal = computeOriginalItemsTotal(order, exchangeData.itemsToExchange);
  const paidForExchangedItems = computePaidForExchangedItems(order, originalItemsTotal);
  const newItemsSubtotal = computeNewItemsCatalogTotal(exchangeData.newProducts);
  const markupAmount = computeNewItemsMarkupAmount(exchangeData.newProducts);
  const adjustedTotal = computeNewItemsAdjustedTotal(exchangeData.newProducts);
  const pricingEnabled =
    (exchangeData.markupConfig?.enabled ?? false) ||
    (exchangeData.discountConfig?.enabled ?? false) ||
    markupAmount > 0 ||
    exchangeData.newProducts.some(
      (product) => product.discountType !== 'NONE' && product.discountValue > 0,
    );
  const newItemsTotal = computeNewItemsExchangeTotal(
    exchangeData.newProducts,
    pricingEnabled,
    resolveFinalPrice,
  );
  const discountAmount = pricingEnabled
    ? getExchangeAppliedDiscount(adjustedTotal, newItemsTotal)
    : 0;
  const isSummaryCoherent = isExchangeSummaryCoherent(
    newItemsSubtotal,
    markupAmount,
    discountAmount,
    newItemsTotal,
  );
  const difference = roundMoney(newItemsTotal - paidForExchangedItems);
  const totalPayments = roundMoney(
    exchangeData.payments?.reduce((sum, p) => sum + Number(p.amount || 0), 0) || 0,
  );
  const remainingBalance = difference > 0 ? roundMoney(Math.max(0, difference - totalPayments)) : 0;
  const change = difference > 0 ? roundMoney(Math.max(0, totalPayments - difference)) : 0;

  let paymentStatus: ExchangeSummary['paymentStatus'] = 'pending';
  if (difference <= 0) {
    paymentStatus = 'complete';
  } else if (totalPayments >= difference) {
    paymentStatus = 'complete';
  } else if (totalPayments > 0) {
    paymentStatus = 'partial';
  }

  return {
    originalItemsTotal,
    paidForExchangedItems,
    newItemsTotal,
    newItemsSubtotal,
    markupAmount,
    discountAmount,
    difference,
    isSummaryCoherent,
    requiresPayment: difference > 0,
    requiresRefund: difference < 0,
    totalPayments,
    remainingBalance,
    change,
    paymentStatus,
  };
}
